import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { NAV } from '../../config-global';
import useLocalStorage from '../../hooks/useLocalStorage';

interface SidebarContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  isProSidebar: boolean;
  sidebarWidth: number;
}

const SidebarContext = createContext<SidebarContextType>({
  sidebarOpen: true,
  setSidebarOpen: () => {},
  toggleSidebar: () => {},
  isProSidebar: false,
  sidebarWidth: 0,
});

interface SidebarProviderProps {
  children: React.ReactNode;
  isProSidebar: boolean;
}

export function SidebarProvider({
  children,
  isProSidebar,
}: SidebarProviderProps) {
  const outerTheme = useTheme();
  const isDesktop = useMediaQuery(outerTheme.breakpoints.up('lg'), {
    noSsr: true,
  });

  const [sidebarOpen, setSidebarOpen] = useLocalStorage<boolean>(
    'pro-sidebar-open',
    true,
  );

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev: boolean) => !prev);
  }, [setSidebarOpen]);

  const sidebarWidth =
    isProSidebar && isDesktop
      ? sidebarOpen
        ? NAV.W_DASHBOARD
        : NAV.W_DASHBOARD_MINI
      : 0;

  const value = useMemo(
    () => ({
      sidebarOpen,
      setSidebarOpen,
      toggleSidebar,
      isProSidebar,
      sidebarWidth,
    }),
    [sidebarOpen, setSidebarOpen, toggleSidebar, isProSidebar, sidebarWidth],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebarContext() {
  return useContext(SidebarContext);
}
